import { PageBlockDefinitionMultiRichTextPropertyStyle } from "../../documentation/block-definitions/item";
import { DesignTokenType } from "../raw-element";
import { PageBlockContextConnection } from "./documentation-block-context";
import { PageBlockCalloutType, PageBlockCodeLanguage, PageBlockText, PageBlockTokenNameFormat, } from "./documentation-block-v1";
import { z } from "zod";
export { PageBlockContextConnection } from "./documentation-block-context";
//
// Enums
//
export const PageBlockLinkType = z.enum([
    "DocumentationItem",
    "PageHeading",
    "Url",
]);
export const PageBlockImageType = z.enum(["Resource", "FigmaNode"]);
export const PageBlockImageAlignment = z.enum(["Left", "Center", "Stretch"]);
export const PageBlockTableCellAlignment = z.enum(["Left", "Center", "Right"]);
export const PageBlockPreviewContainerSize = z.enum([
    "Centered",
    "NaturalHeight",
]);
export const PageBlockThemeDisplayMode = z.enum(["Split", "Override"]);
export const PageBlockTokenValueFormat = z.enum([
    "ResolvedValue",
    "ReferenceName",
    "NoValue",
]);
//
// Definitions - Block
//
export const PageBlockImageResourceReference = z.object({
    resourceId: z.string(),
    url: z.string(),
});
export const PageBlockResourceFrameNodeReference = z.object({
    sourceId: z.string(),
    frameReferenceId: z.string(),
});
export const PageBlockImageReference = z.object({
    type: PageBlockImageType,
    resource: PageBlockImageResourceReference.optional(),
    figmaNode: PageBlockResourceFrameNodeReference.optional(),
});
export const PageBlockColorV2 = z.object({
    value: z.string(),
    referencedTokenId: z.string().optional(),
});
export const PageBlockAssetEntityMeta = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    backgroundColor: PageBlockColorV2.optional(),
    showNestedGroups: z.boolean().optional(),
});
export const PageBlockFigmaComponentEntityMeta = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    backgroundColor: PageBlockColorV2.optional(),
    selectedComponentProperties: z.array(z.string()).optional(),
});
export const PageBlockFigmaNodeEntityMeta = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    backgroundColor: PageBlockColorV2.optional(),
});
export const PageBlockAppearanceV2 = z.object({
    itemBackgroundColor: PageBlockColorV2.optional(),
    numberOfColumns: z.number().optional(),
});
export const PageBlockItemUntypedValue = z
    .object({
    value: z.any(),
})
    .and(z.record(z.any()));
export const PageBlockLinkV2 = z.object({
    type: PageBlockLinkType,
    documentationItemId: z.string().optional(),
    pageHeadingId: z.string().optional(),
    url: z.string().optional(),
    openInNewTab: z.boolean().optional(),
});
export const PageBlockItemV2 = z.object({
    id: z.string(),
    linksTo: PageBlockLinkV2.optional(),
    props: z.record(PageBlockItemUntypedValue),
});
export const PageBlockDataV2 = z.object({
    packageId: z.string(),
    variantId: z.string().optional(),
    indentLevel: z.number(),
    appearance: PageBlockAppearanceV2.optional(),
    items: z.array(PageBlockItemV2),
});
//
// Definitions - Typed Values
//
export const PageBlockItemAssetValue = z.object({
    selectedPropertyIds: z.array(z.string()).optional(),
    showSearch: z.boolean().optional(),
    showAssetDescription: z.boolean().optional(),
    previewContainerSize: PageBlockPreviewContainerSize.optional(),
    previewContainerHeight: z.number().optional(),
    backgroundColor: PageBlockColorV2.optional(),
    value: z
        .array(z.object({
        entityId: z.string(),
        entityType: z.enum(["Asset", "AssetGroup"]),
        entityMeta: PageBlockAssetEntityMeta.optional(),
    }))
        .default([]),
});
export const PageBlockItemAssetPropertyValue = z.object({
    value: z.array(z.string()).default([]),
});
export const PageBlockItemFigmaComponentValue = z.object({
    showComponentName: z.boolean().optional(),
    showComponentDescription: z.boolean().optional(),
    showPropertyList: z.boolean().optional(),
    previewOrderIds: z.array(z.string()).optional(),
    previewContainerSize: PageBlockPreviewContainerSize.optional(),
    previewContainerHeight: z.number().optional(),
    backgroundColor: PageBlockColorV2.optional(),
    value: z
        .array(z.object({
        entityId: z.string(),
        entityType: z.enum(["FigmaComponent"]),
        entityMeta: z
            .object({
            selectedComponentProperties: z.string().array().optional(),
            selectedComponentVariants: z.record(z.string().array()).optional(),
        })
            .optional(),
    }))
        .default([]),
});
export const PageBlockItemBooleanValue = z.object({
    value: z.boolean(),
});
export const PageBlockItemCodeValue = z.object({
    format: PageBlockCodeLanguage.optional(),
    caption: z.string().optional(),
    value: z.string(),
});
export const PageBlockItemSandboxValue = z.object({
    showCode: z.boolean().optional(),
    showControls: z.boolean().optional(),
    backgroundColor: z.string().optional(),
    alignPreview: z.enum(["Left", "Center"]).optional(),
    previewHeight: z.number().optional(),
    value: z.string(),
});
export const PageBlockItemColorValue = z.record(z.any());
export const PageBlockItemComponentValue = z.object({
    selectedPropertyIds: z.array(z.string()).optional(),
    selectedBrandId: z.string().optional(),
    value: z
        .array(z.object({
        entityId: z.string(),
        entityType: z.enum(["Component", "ComponentGroup"]),
    }))
        .default([]),
});
export const PageBlockItemComponentPropertyValue = z.object({
    value: z.string(),
});
export const PageBlockItemDividerValue = z.object({});
export const PageBlockItemEmbedValue = z.object({
    value: z.string().optional(),
    caption: z.string().optional(),
    height: z.number().optional(),
    alt: z.string().optional(),
    openGraph: z
        .object({
        title: z.string().optional(),
        description: z.string().optional(),
        imageUrl: z.string().optional(),
    })
        .optional(),
});
export const PageBlockItemFigmaNodeValue = z.object({
    showSearch: z.boolean().optional(),
    previewContainerSize: PageBlockPreviewContainerSize.optional(),
    previewContainerHeight: z.number().optional(),
    backgroundColor: PageBlockColorV2.optional(),
    showFrameDetails: z.boolean().optional(),
    value: z
        .array(z.object({
        entityId: z.string(),
        entityMeta: PageBlockFigmaNodeEntityMeta.optional(),
    }))
        .default([]),
});
export const PageBlockItemImageValue = z.object({
    alt: z.string().optional(),
    caption: z.string().optional(),
    alignment: PageBlockImageAlignment.optional(),
    openLightbox: z.boolean().optional(),
    isBordered: z.boolean().optional(),
    value: PageBlockImageReference.optional(),
});
export const PageBlockItemMarkdownValue = z.object({
    value: z.string(),
});
export const PageBlockItemMultiRichTextValue = z.object({
    value: PageBlockText.array(),
});
export const PageBlockItemMultiSelectValue = z.object({
    value: z.array(z.string()).default([]),
});
export const PageBlockItemNumberValue = z.object({
    value: z.number(),
});
export const PageBlockItemRichTextValue = z.object({
    value: PageBlockText,
    calloutType: PageBlockCalloutType.optional(),
});
export const PageBlockItemSingleSelectValue = z.object({
    value: z.string(),
});
export const PageBlockItemStorybookValueOld = z.object({
    value: z.string().optional(),
    caption: z.string().optional(),
    height: z.number().optional(),
    embedUrl: z.string().optional(),
    storyId: z.string().optional(),
    sourceId: z.string().optional(),
});
export const PageBlockItemStorybookValue = z.object({
    value: z
        .array(z.object({
        entityId: z.string().optional(),
        embed: z
            .object({
            inputUrl: z.string(),
            embedUrl: z.string(),
        })
            .optional(),
        entityMeta: z
            .object({
            caption: z.string().optional(),
            height: z.number().optional(),
            hiddenProps: z.array(z.string()).optional(),
            propValues: z.record(z.unknown()).optional(),
        })
            .optional(),
    }))
        .optional(),
    showCode: z.boolean().optional(),
    showFooter: z.boolean().optional(),
    showProperties: z.boolean().optional(),
    showDescription: z.boolean().optional(),
    showDefaults: z.boolean().optional(),
    showThemeSwitcher: z.boolean().optional(),
});
export const PageBlockItemTextValue = z.object({
    value: z.string(),
});
export const PageBlockItemSwatch = z.object({
    id: z.string(),
    selectedThemeIds: z.string().array(),
});
export const PageBlockItemTokenValue = z.object({
    selectedPropertyIds: z.array(z.string()).optional(),
    selectedThemeIds: z.array(z.string()).optional(),
    swatches: z.array(PageBlockItemSwatch).optional(),
    themeDisplayMode: PageBlockThemeDisplayMode.optional(),
    tokenNameFormat: PageBlockTokenNameFormat.optional(),
    tokenNameCustomPropertyId: z.string().optional(),
    tokenValueFormat: PageBlockTokenValueFormat.optional(),
    value: z
        .array(z.object({
        entityId: z.string(),
        entityType: z.enum(["Token", "TokenGroup"]),
        entityMeta: z
            .object({
            showNestedGroups: z.boolean().optional(),
        })
            .optional(),
    }))
        .default([]),
});
export const PageBlockItemFileValue = z.object({
    value: z
        .object({
        entityId: z.string(),
        entityMeta: z
            .object({
            title: z.string().optional(),
            description: z.string().optional(),
        })
            .optional(),
    })
        .array(),
});
export const PageBlockItemContextValue = z.object({
    value: z
        .array(z.object({
        id: z.string(),
    }))
        .default([]),
    connections: z.array(PageBlockContextConnection).default([]),
    title: z.string().optional(),
    showDescription: z.boolean().optional(),
    description: z.string().optional(),
    showMcpUrl: z.boolean().optional(),
    showCodeSnippet: z.boolean().optional(),
});
export const PageBlockItemTokenPropertyValue = z.object({
    selectedPropertyIds: z.array(z.string()).optional(),
    selectedThemeIds: z.array(z.string()).optional(),
    value: z.array(z.string()).default([]),
});
export const PageBlockItemTokenTypeValue = z.object({
    value: z.array(DesignTokenType).default([]),
});
export const PageBlockItemUrlValue = z.object({
    value: z.string(),
});
//
// Definitions - Rich text editor value
//
export const PageBlockItemRichTextEditorParagraphNode = z.object({
    type: z.literal("Paragraph"),
    value: PageBlockItemRichTextValue.shape.value,
});
export const PageBlockItemRichTextEditorListNode = z.object({
    type: z.literal("List"),
    listType: PageBlockDefinitionMultiRichTextPropertyStyle,
    value: PageBlockItemMultiRichTextValue.shape.value,
});
export const PageBlockItemRichTextEditorNode = z.discriminatedUnion("type", [
    PageBlockItemRichTextEditorParagraphNode,
    PageBlockItemRichTextEditorListNode,
]);
export const PageBlockItemRichTextEditorValue = z.object({
    value: PageBlockItemRichTextEditorNode.array(),
});
//
// Definitions - Table Value
//
export const PageBlockItemTableRichTextNode = z.object({
    type: z.literal("RichText"),
    id: z.string(),
    value: PageBlockItemRichTextValue.shape.value,
});
export const PageBlockItemTableImageNode = z.object({
    type: z.literal("Image"),
    id: z.string(),
    caption: PageBlockItemImageValue.shape.caption,
    alt: PageBlockItemImageValue.shape.alt,
    value: PageBlockItemImageValue.shape.value,
    alignment: PageBlockItemImageValue.shape.alignment,
    openLightbox: PageBlockItemImageValue.shape.openLightbox,
    isBordered: PageBlockItemImageValue.shape.isBordered,
});
export const PageBlockItemTableNode = z.discriminatedUnion("type", [
    PageBlockItemTableRichTextNode,
    PageBlockItemTableImageNode,
]);
export const PageBlockItemTableCell = z.object({
    id: z.string(),
    nodes: z.array(PageBlockItemTableNode),
    columnWidth: z.number().optional(),
    alignment: PageBlockTableCellAlignment,
});
export const PageBlockItemTableRow = z.object({
    cells: z.array(PageBlockItemTableCell),
});
export const PageBlockItemTableValue = z.object({
    highlightHeaderColumn: z.boolean().optional(),
    highlightHeaderRow: z.boolean().optional(),
    showBorder: z.boolean().optional(),
    value: z.array(PageBlockItemTableRow).default([]),
});
//
// Definitions - Conversion
//
export function storybookValueFromOldValue(oldValue) {
    return {
        value: [
            {
                // URLs
                ...(oldValue.embedUrl &&
                    oldValue.value && {
                    embed: {
                        embedUrl: oldValue.embedUrl,
                        inputUrl: oldValue.value,
                    },
                }),
                // Meta
                entityMeta: {
                    caption: oldValue.caption,
                    height: oldValue.height,
                },
            },
        ],
    };
}
